'use client';

import { useEffect, useRef, useState } from 'react';
import { fetchEuroCupChatroomUrl, useFetchChatroomToken } from '@/hooks/query/use-chatroom';
import { UserInfo } from '@nf/types/account';
import { SiteConfigsSettingsData } from '@nf/types/site';
import { Button } from '@nf/ui';
import { Icon } from '@/components/icons/svg-icons';
import { Breakpoint } from '@/features/breakpoint';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { useStreamingStore } from '@/store/streaming';
import { useStreamerWatchTimeTracking } from '@/hooks/query/use-reward-system';
import { SportMatchModel } from '@nf/types/odds';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAccountInfo } from '@/hooks/query/account';
import { streamingService } from '@/services/streaming.service';
import { GetGUID, getBetFrom, TranslateLanguageToDC } from '@/utils/common-util';
import { useAppearanceStore } from '@/store/appearance';
import { isDesktop } from 'react-device-detect';
import { ViewEnum } from '@/types';
import discoverPluginService from '@/services/discover-plugin.service';

interface MatchData {
	RealMatchId: number;
}

export const StreamerPlugin = ({ match, streamingPluginType }: { match: SportMatchModel, streamingPluginType: number }) => {
	const [
		isExpnadChatroom,
		setIsExpnadChatroom,
		streamingPluginUrl,
		setStreamingPluginUrl,
		isStreamingFloat,
		setIsStreamingFloat,
		streamingType,
		setStreamingType,
	] = useStreamingStore(
		state => [
			state.isExpnadChatroom,
			state.setIsExpnadChatroom,
			state.streamingPluginUrl,
			state.setStreamingPluginUrl,
			state.isStreamingFloat,
			state.setIsStreamingFloat,
			state.streamingType,
			state.setStreamingType,
		]
	);
	
	const view = useAppearanceStore(state => state.view);
	const { user } = useAccountInfo();
	const currentRef = useRef<HTMLDivElement>(null);
	const { breakpoint } = useBreakpoint();
	const { SiteConfigsData } = useGetSiteConfigs();
	const pluginIframeRef = useRef<HTMLIFrameElement>(null);
	const [isExpnadGift, setIsExpnadGift] = useState(false);
	const { sourceInfoList } = streamingService.getVideoInfoList(match, SiteConfigsData, user);
	const [topPosition, setTopPosition] = useState(currentRef.current?.getBoundingClientRect().top ?? 166);
	const [streamingPluginKey, setStreamingPluginKey] = useState('');
	const [fetchPluginData, setFetchPluginData] = useState('');
	const [isHorizontal, setIsHorizontal] = useState(false); 
	const isMobileDevice = () => {
		return window.matchMedia('(orientation: portrait)').matches
			? window.innerWidth < 578
			: window.innerHeight < 500;
	}
	
	const onOrientationChange = () => {

		if (!isMobileDevice()) {
			return;
		}
		
		const iframeContentWindow = pluginIframeRef.current?.contentWindow;
		iframeContentWindow?.postMessage({
			type: 'SetChatroomViewPortInfo',
			action: { parentWidth: window.innerWidth, parentHeight: window.innerHeight, isHorizontal: null },
		}, '*');

		setIsExpnadGift(false);
		setIsExpnadChatroom(false);
	}

	useEffect(() => {
		const updateIsHorizontal = () => {
			setIsHorizontal(!window.matchMedia('(orientation: portrait)').matches);
		};
		
		window.addEventListener('orientationchange', updateIsHorizontal);
		window.addEventListener('resize', updateIsHorizontal);
		return () => {
			window.removeEventListener('orientationchange', updateIsHorizontal);
			window.removeEventListener('resize', updateIsHorizontal);
		};
	}, []);
	
	const getSrcList = () => {
		const srcList: number[] = []
		if (sourceInfoList.length > 0) {
			sourceInfoList.forEach((src) => {
				if (src.smsc && src.smsc > 19 && !srcList.includes(src.smsc)) {
					srcList.push(src.smsc);
				}
			})
		}
		return srcList.length > 0 ? srcList : [];
	}

	const matchData = {
		RealMatchId: match.MatchId,
		parentId: match.Parentid || 0
	}
	const fetchChatroomTokenData = {
		match: matchData,
		SmartUrl: window.location.origin,
		ApibackendUrl: user?.ApiBackendUrl || '',
		AccessToken: user?.AccessToken || '',
		VenderMemberId: user?.VenderMemberId || ''
	}
	const { refetch: refetchFetchChatroomToken } = useFetchChatroomToken(fetchChatroomTokenData);

	const GetIframeUrlKey = () => {
		const date = Date.now();
		return `${date}-${streamingPluginType}`;
	}

	const GetUserInfo = (user: UserInfo, siteConfigsData: SiteConfigsSettingsData, view: ViewEnum) => {
		const lang = TranslateLanguageToDC(user.Lang || 'en-US');
		return {
			SiteId: siteConfigsData.GetMessageID,
			SiteName: siteConfigsData.SiteName,
			SubsidiaryName: user.SubsidiaryOpratorId,
			CustId: user.ID,
			LicenseeUserName: user.DisplayUserName,
			CurrencyId: user.CurrId,
			CurrencyName: user.Curr,
			BetFrom: getBetFrom(view, isDesktop),
			EnableSingleWallet: user.EnableSingleWallet,
			UserLanguage: lang.toUpperCase(),
			TsId: user.TsId,
			BaStatus: user.BaStatus,
			EnableDefaultOpenChatroom: siteConfigsData.EnableDefaultOpenChatroom || (breakpoint === Breakpoint.lg),
			VenderMemberId: user.VenderMemberId,
			NickName: user.Niname ?? '',
		}
	}

	const handlePluginData = async (SiteConfigsData: SiteConfigsSettingsData, user: UserInfo, matchData: MatchData, view: ViewEnum) => {
		const chatroomToken = await refetchFetchChatroomToken();
		if (!!chatroomToken?.data?.data?.Data) {
			const pData = {
				Seq: GetGUID(),
				UserInfo: GetUserInfo(user, SiteConfigsData, view),
				MatchInfo: matchData,
				MaskedStreamingSrc: discoverPluginService.getMaskedStreamingSrc(SiteConfigsData, user),
				ExclusiveStreamerType: SiteConfigsData.ExclusiveStreamerType,
				StreamingPluginType: streamingPluginType,
				IsDarkMode: user.DarkMode,
				Token: chatroomToken?.data?.data?.Data,
				SmartUrl: window.location.origin,
				IsFocusGV: SiteConfigsData?.EnableSlmtGv && streamingType === 'SlmtGV'
			}
			return JSON.stringify(pData)

		} else {
			return '';
		}
	};
	useEffect(() => {
		handleAsyncPluginData();

		return () => {
			window.removeEventListener('message', ExpandChatroom);
			setIsExpnadChatroom(false);
		}
	}, [streamingPluginType, match.MatchId]);
	
	useEffect(() => {
		onOrientationChange();
	}, [isHorizontal])


	const handleAsyncPluginData = async () => {

		if (SiteConfigsData && user?.ActStatus === 2) {
			try {
				const data = await handlePluginData(SiteConfigsData, user, matchData, view);
				setFetchPluginData(data);
				window.addEventListener('message', ExpandChatroom);
			} catch (error) {
				console.error('Error handling plugin data', error);
				setStreamingPluginUrl('');
			}
		}
	};

	useEffect(() => {
		if (fetchPluginData && SiteConfigsData?.DiscoverChatroomApiUrl) {
			fetchEuroCupChatroomUrl(fetchPluginData, SiteConfigsData?.DiscoverPluginUrl).then((ChatroomUrlData) => {
				if (ChatroomUrlData?.data?.Code == 200 && ChatroomUrlData?.data?.Result?.Url) {
					setStreamingPluginUrl(ChatroomUrlData?.data?.Result?.Url || '');
					setStreamingPluginKey(GetIframeUrlKey());
				} else {
					setStreamingPluginUrl('');
				}
			});
		}
	}, [fetchPluginData, SiteConfigsData?.DiscoverChatroomApiUrl]);

	useEffect(() => {
		const iframeContentWindow = pluginIframeRef.current?.contentWindow;
		if (breakpoint === Breakpoint.lg && iframeContentWindow && isExpnadChatroom) {
			iframeContentWindow.postMessage({ type: 'ChatRoomExpandIcon', action: { IsDisplay: 0 } }, '*');
		}

	}, [isExpnadChatroom, breakpoint]);

	const handleResize = () => {
		const iframeContentWindow = pluginIframeRef.current?.contentWindow;
		if (breakpoint === Breakpoint.lg && iframeContentWindow && streamingPluginUrl) {
			setIsExpnadChatroom(true);
			iframeContentWindow.postMessage({ type: 'ChatRoomExpandIcon', action: { IsDisplay: 0 } }, '*');
			iframeContentWindow.postMessage({ type: 'StreamerChatRoom', action: { expandChat: 1 } }, '*');
		}
	};

	useEffect(() => {
		handleResize();
		const resizeObserver = new ResizeObserver(handleResize);
		resizeObserver.observe(document.documentElement);
		return () => {
			resizeObserver.disconnect();
			setStreamingPluginUrl('')
		};
	}, []);


	const ExpandChatroom = (e: MessageEvent) => {
		if (e.data.type === 'StreamerChatRoom') {
			if (e.data.action.expandChat === 1) {
				setIsExpnadChatroom(true);
				setIsExpnadGift(false);
			} else {
				if (e.data.action.expandGift === 1) {
					setIsExpnadGift(true);
				} else {
					setIsExpnadGift(false);
					setIsExpnadChatroom(false);
				}
			}
		}
	}

	const handleOnLoad = () => {
		setTopPosition(currentRef.current?.getBoundingClientRect().top ?? 166);
	}

	const handleIframeClose = () => {
		setIsStreamingFloat(false);
		setStreamingType('');
	};

	useStreamerWatchTimeTracking(user?.RewardInfo?.StreamerRewardId);

	if (!streamingPluginUrl || streamingPluginUrl === 'clear') return null;

	const chatroomIframe = (
		<div className={`chatroom ${isHorizontal && 'chatroom--horizontal'}`}
			key={streamingPluginKey}
			ref={currentRef}
			data-chatroom-open={isExpnadChatroom}
			data-chatroom-gift-open={isExpnadGift}
			style={{ top: topPosition + 'px' }}
		>
			<iframe
				id="chatroom-iframe"
				ref={pluginIframeRef}
				src={streamingPluginUrl}
				onLoad={handleOnLoad}
			>
			</iframe>
		</div>
	)

	const streamingIframe = (
		<div className="video-iframe video-view-ref"
			ref={currentRef}
			style={{ top: topPosition + 'px' }}
			key={streamingPluginKey}
		>
			{isStreamingFloat ?
				<Button className="btn--icon btn--close" onClick={handleIframeClose}>
					<Icon name="close-big" />
				</Button>
				: null}
			<iframe
				ref={pluginIframeRef}
				src={streamingPluginUrl}
				onLoad={handleOnLoad}
				allow="fullscreen"
			>
			</iframe>
		</div>
	)

	const streamingChatroom = (
		<div
			// className="video-chatroom"
			className={`video-chatroom ${isHorizontal ? 'video-chatroom--horizontal' :''}`}
			ref={currentRef}
			data-chatroom-open={isExpnadChatroom}
			style={{ top: topPosition + 'px' }}
			key={streamingPluginKey}
		>
			<iframe
				ref={pluginIframeRef}
				src={streamingPluginUrl}
				onLoad={handleOnLoad}
				allow="fullscreen"
			>
			</iframe>
		</div>
	);
	switch (streamingPluginType) {
		case 1:
			return streamingIframe;
		case 2:
			return streamingChatroom;
		case 3:
			return chatroomIframe;
		default:
			return null;
	}
};

